import { useEffect, useRef, useState } from 'react';

import { Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { closeDrawer, selectNameInConflict, selectOpen,setNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { postAuthorizedIssuer } from 'common/services/api/AuthorizedIssuers/AuthorizedIssuers.thunk';
import { fetchIdentityProviders } from 'common/services/api/IdentityProviders/IdentityProviders.thunks';
import { IdentityProvider } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

const AUTHORIZED_ISSUER_CREATE_SUCCESS_MESSAGE = 'The Authorized Issuer was successfully created!';
const EXISTING_AUDIENCE_ERROR_MESSAGE = 'The same set of Tenant, Identity Provider and Audience already exist';
const EXISTING_AUDIENCE_ERROR_INLINE_STYLES = {
    color: 'error.main',
    marginTop: '10px !important',
    fontSize: '12px'
};

type AuthorizedIssuerCreateContentProps = Readonly<{
    title: string,
    tenantName: string,
    saveButtonFunction: () => void,
    processing: boolean,
    audienceInConflict: boolean,
    handleAudienceInput: () => void,
    audienceFieldRef: React.RefObject<HTMLInputElement | null>,
    identityProvider: IdentityProvider | null,
    handleChangeIdentityProvider: (e: unknown, newIdentityProvider: IdentityProvider | null) => void
}>;

const constructCreateContentComponent = (
    {
        title,
        saveButtonFunction,
        processing,
        audienceFieldRef,
        audienceInConflict,
        handleAudienceInput,
        identityProvider,
        handleChangeIdentityProvider,
        tenantName
    }: AuthorizedIssuerCreateContentProps
) => (
    <EditDrawer
        title={title}
        saveButtonFunction={saveButtonFunction}
        processing={processing}
    >
        <Stack spacing={2}>
            <EntitiesSearchableCommonDropDown
                initialEntityOption={{ id: '', name: '', issuer: '' }}
                value={identityProvider}
                onChange={handleChangeIdentityProvider}
                label='Identity Provider'
                getOptionLabel={getOptionLabelDefaultCallback}
                isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                asyncThunkAction={fetchIdentityProviders}
                asyncThunkArgs={undefined}
                displayMenuItemContent={(option: IdentityProvider) => <>{option.name}</> }
                entityPlaceholder='Select Identity Provider'
            />
            <TextField
                inputRef={audienceFieldRef}
                size="small"
                label="Authorized Issuer Audience"
                placeholder="Type Authorized Issuer Audience"
                variant="filled"
                onChange={handleAudienceInput}
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Tenant Name"
                variant="filled"
                defaultValue={tenantName}
                disabled
            />
            {audienceInConflict &&
                <ErrorHandler
                    errorMessage={EXISTING_AUDIENCE_ERROR_MESSAGE}
                    customInlineStyles={EXISTING_AUDIENCE_ERROR_INLINE_STYLES}
                />
            }
        </Stack>
    </EditDrawer>
);

export function AuthorizedIssuerCreateContent({
    title
}: EditDrawerContentProps) {
    const audienceFieldRef = useRef<HTMLInputElement>(null);
    const tenantDetails = useAppSelector(selectPageInfo);
    const { enqueueSnackbar } = useSnackbar();

    const [currentIdentityProvider, setCurrentIdentityProvider] = useState<IdentityProvider | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const nameInConflict = useAppSelector(selectNameInConflict);
    const isOpenDrawer = useAppSelector(selectOpen);

    const dispatch = useAppDispatch();
    const authorizedIssuerPostCallback = () => {
        setProcessing(true);
        const authorizedIssuerOptions = {
            tenantId: tenantDetails?.id ?? '',
            audience: audienceFieldRef.current?.value ?? '',
            identityProviderId: currentIdentityProvider?.id ?? ''
        };
        dispatch(postAuthorizedIssuer(authorizedIssuerOptions))
            .unwrap()
            .then((result) => {
                enqueueSnackbar(AUTHORIZED_ISSUER_CREATE_SUCCESS_MESSAGE, { variant: 'success' });
                dispatch(setRows(result.rows));
                dispatch(setNameInConflict(false));
                dispatch(closeDrawer());
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Create, extractStatusCode(error.message), 'Authorized Issuer', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    };

    const handleAudienceInput = () => {
        dispatch(setSaveButtonEnabled(!!audienceFieldRef?.current?.value && !!currentIdentityProvider?.id));
    };

    const handleChangeIdentityProvider = (_e: unknown, newIdentityProvider: IdentityProvider | null) => {
        setCurrentIdentityProvider(newIdentityProvider);
    };

    useEffect(() => {
        handleAudienceInput();
    }, [currentIdentityProvider?.id]);

    useEffect(() => {
        setCurrentIdentityProvider(null);
    }, [isOpenDrawer]);

    const authorizedIssuersCreateContentProps: AuthorizedIssuerCreateContentProps = {
        title,
        saveButtonFunction: authorizedIssuerPostCallback,
        processing,
        audienceInConflict: nameInConflict ?? false,
        handleAudienceInput,
        handleChangeIdentityProvider,
        tenantName: tenantDetails?.name ?? '',
        audienceFieldRef,
        identityProvider: currentIdentityProvider
    };

    return constructCreateContentComponent(authorizedIssuersCreateContentProps);
}
